import { keyBy } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { v4 as uuid } from 'uuid';

import { SearchableFields } from '~/api/dataFields';
import { getHandlersKeyFromSearchResult } from '~/components/SearchV2/SearchResult/SearchResult.utils';
import BaseBooleanSearchBar from '~/components/Shared/BaseBooleanSearchBar/BaseBooleanSearchBar';
import { useDocumentMultipleLocationsModal } from '~/components/Shared/EcModal/useDocumentMultipleLocationsModal';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { Table } from '~/eds/src/modules/Table/Table';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { api } from '~/redux/api/createApi';
import { ERROR } from '~/types/toast.types';
import Box from '~/ui/components/Box/Box';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import LoadingContainer from '~/ui/components/LoadingContainer/LoadingContainer';
import Text from '~/ui/components/Text/Text';
import {
  DEFAULT_TABLE_SORT_COLUMN_ORDER,
  HandlerName,
  renderCellValue,
} from '~/utils/table';

function SearchDocumentsToAddToGroup({ onDocumentsSelect }) {
  const intl = useIntl();
  const formatNumber = useFormatNumber();
  const [booleanQuery, setBooleanQuery] = useState('');
  const [query, setQuery] = useState();
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [sortBy, setSortBy] = useState(DEFAULT_TABLE_SORT_COLUMN_ORDER);
  const [columnOrder, setColumnOrder] = useState([]);
  const [fields, setQueryFields] = useState();

  const [setDocumentSelected, renderMultipleLocationModal] =
    useDocumentMultipleLocationsModal(getHandlersKeyFromSearchResult, true);

  const {
    data: tableViewSet,
    isSuccess: isGetTableViewSetSuccess,
    isFetching: isFetchingTableViewSet,
  } = api.endpoints.getCurrentTableView.useQuery({ context: 'GROUP' });

  const {
    data: filterSections,
    isSuccess: isGetFilterSectionsSuccess,
    isFetching: isFetchingFilterSections,
  } = api.endpoints.getFilterSections.useQuery(undefined);

  const dataFields = filterSections?.dataFields;

  const searchQueryParams = useMemo(() => {
    return {
      query,
      fields,
      page,
      pageSize,
      sortBy,
    };
  }, [query, fields, page, pageSize, sortBy]);

  const { data, error, isFetching } = api.endpoints.searchDocuments.useQuery(
    searchQueryParams,
    {
      skip: !query,
    },
  );

  useEffect(() => {
    if (error) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'SearchDocumentsToAddToGroup.searchError',
          defaultMessage: 'An error occurred while searching for documents.',
          description: 'Error message when document search fails',
        }),
      );
    }
  }, [error]);

  const updateQueryFields = (updatedColumnOrder) => {
    const isDataFieldId = (columnKey) =>
      !!filterSections?.fields[columnKey] &&
      SearchableFields.DocumentGroup !== columnKey;

    const toDataField = (columnKey) => filterSections?.fields[columnKey];

    const queryFields = updatedColumnOrder
      .filter(isDataFieldId)
      .map(toDataField);

    setQueryFields(queryFields);
  };

  const stickyColumnOrder = ['name'];
  useEffect(() => {
    if (isGetTableViewSetSuccess && isGetFilterSectionsSuccess) {
      const currentColumnOrder = tableViewSet.map((column) => column.field_id);
      const defaultColumnOrder = [...stickyColumnOrder, ...currentColumnOrder];
      setColumnOrder(defaultColumnOrder);
      updateQueryFields(defaultColumnOrder);
    }
  }, [isGetTableViewSetSuccess, isGetFilterSectionsSuccess]);

  const isLoading =
    isFetchingTableViewSet || isFetchingFilterSections || isFetching;

  const handlePaginate = ({ pageIndex }) => {
    setPage(pageIndex);
  };

  const handleToggleSortBy = (newSortBy) => {
    if (newSortBy.id !== sortBy.id) {
      newSortBy.desc = true;
    }
    setSortBy(newSortBy);
  };

  const handleUpdate = (state, action) => {
    onDocumentsSelect(state.selectedRowIds);

    if (action?.type === 'toggleSortBy') {
      handleToggleSortBy(state.sortBy[0]);
    }
  };

  const handleSearchBarSubmit = () => {
    setPage(1);
    const query = {
      booleanQuery,
      filters: [
        {
          fieldId: 'document_group_id',
          id: uuid(),
          operatorId: 'is_blank',
          values: [],
        },
      ],
      fields: filterSections?.fields,
    };
    setQuery(query);
  };

  const hardcodedColumns = [
    {
      key: 'name',
      title: intl.formatMessage({
        id: 'SearchDocumentsToAddToGroup.nameColumn',
        defaultMessage: 'Name',
        description: 'Column header for document name',
      }),
      minWidth: 'm',
      renderCell: (document) => {
        const handlers = getHandlersKeyFromSearchResult(document);
        return (
          <HandlerName
            handlers={handlers}
            openTarget="_blank"
            onClick={() => setDocumentSelected(document)}
          />
        );
      },
    },
  ];

  const hardcodedColumnsMapByKey = keyBy(hardcodedColumns, 'key');
  const hardcodedColumnsMapByTitle = keyBy(hardcodedColumns, 'title');

  const wasNotHardcodedAlready = (field) =>
    !hardcodedColumnsMapByKey[field.id] &&
    !hardcodedColumnsMapByTitle[field.name];

  const fieldToColumn = (field) => ({
    key: String(field.id),
    field,
    title: field.label,
    renderCell: (document) => {
      return (
        <Text shouldTruncate={true} color="gray-900" variant="s-dense">
          {renderCellValue(field, formatNumber, document.selected_field_values)}
        </Text>
      );
    },
  });

  const fieldsToColumns = (fieldItems) => {
    return fieldItems.map(fieldToColumn);
  };

  const columns = dataFields
    ? [
        ...hardcodedColumns,
        ...fieldsToColumns(dataFields.filter(wasNotHardcodedAlready)),
      ]
    : hardcodedColumns;

  const hasResults = !error && data?.results.length > 0;
  const noResult = !error && data?.results.length === 0;
  const noSearch = !error && !data;

  return (
    <>
      <Box mb={8}>
        <BaseBooleanSearchBar
          isSubmitting={isFetchingFilterSections}
          onSubmit={handleSearchBarSubmit}
          value={booleanQuery}
          onChange={setBooleanQuery}
        />
      </Box>
      <Box sx={{ overflowY: 'scroll' }}>
        <LoadingContainer isLoading={isLoading || !columns.length}>
          {hasResults && (
            <>
              <Table
                name={intl.formatMessage({
                  id: 'SearchDocumentsToAddToGroup.searchResultTable',
                  defaultMessage: 'search result',
                  description: 'Accessibility name for search results table',
                })}
                totalCount={data.count}
                columns={columns}
                data={data.results}
                state={{
                  pageIndex: page,
                  pageSize,
                  sortBy: [sortBy],
                  columnOrder,
                }}
                onUpdate={handleUpdate}
                onPaginate={handlePaginate}
                options={{
                  enableExportXlsx: false,
                  enableManageColumns: false,
                }}
                reactTableOptions={{
                  autoResetSortBy: false,
                  disableSortRemove: true,
                  manualSortBy: true,
                }}
              />
              {renderMultipleLocationModal()}
            </>
          )}
          {noResult && (
            <FlexLayout
              alignItems="center"
              justifyContent="center"
              sx={{ height: '100px' }}
            >
              <Text variant="subtitle">
                {intl.formatMessage({
                  id: 'SearchDocumentsToAddToGroup.noDocumentsFound',
                  defaultMessage: 'No documents found',
                  description:
                    'Message when no documents match search criteria',
                })}
              </Text>
            </FlexLayout>
          )}
          {noSearch && (
            <FlexLayout
              alignItems="center"
              justifyContent="center"
              sx={{ height: '100px' }}
            >
              <Text variant="subtitle">
                {intl.formatMessage({
                  id: 'SearchDocumentsToAddToGroup.enterSearchQuery',
                  defaultMessage: 'Enter a search query to see results',
                  description: 'Prompt for user to enter search query',
                })}
              </Text>
            </FlexLayout>
          )}
        </LoadingContainer>
      </Box>
    </>
  );
}

export default SearchDocumentsToAddToGroup;
